import React from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { getAccentColor, getBodyColor } from './shared';

// Modern, vibrant, yet professional palette for schools
const MODERN_BRAND_COLORS = ['#4F46E5', '#0EA5E9', '#10B981', '#F43F5E', '#8B5CF6', '#F59E0B'];

const IconGridSection = ({
  section,
  sectionKey,
  sectionIndex,
  iconCards = [],
  iconGridColumns,
  renderHtmlTextBlock,
  renderPositionableElement,
}) => {
  const accent = getAccentColor(section) || '#0F172A';
  const bodyColor = getBodyColor(section) || '#64748B';
  const columns = Math.max(2, Math.min(iconGridColumns || 3, 4));
  
  const themeColors = section.cardColors || MODERN_BRAND_COLORS;

  return (
    <Box component="section" sx={{ py: { xs: 1.1, md: 1.8 }, px: 0, width: '100%' }}>
      <Stack spacing={6} sx={{ width: '100%' }}>
        
        {/* ULTRA-MODERN HEADER */}
        {(section.title || section.body) && (
          <Stack 
            spacing={2} 
            alignItems={section.textAlign === 'center' ? 'center' : 'flex-start'}
            sx={{ textAlign: section.textAlign || 'left', maxWidth: 800, mx: section.textAlign === 'center' ? 'auto' : 0 }}
          >
            {/* Optional modern "badge" or overline */}
            {section.subtitle && (
              <Typography 
                sx={{ 
                  color: accent, 
                  fontWeight: 700, 
                  fontSize: '0.875rem', 
                  letterSpacing: '0.15em', 
                  textTransform: 'uppercase' 
                }}
              >
                {section.subtitle}
              </Typography>
            )}
            
            {section.title && (
              <Typography 
                component="h2" 
                sx={{ 
                  color: '#0F172A', 
                  fontWeight: 800, 
                  fontSize: { xs: '2rem', md: '2.75rem' }, 
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1 
                }}
              >
                {section.title}
              </Typography>
            )}
            
            {section.body && renderPositionableElement({
              section,
              index: sectionIndex,
              elementKey: `${sectionKey}-intro`,
              children: renderHtmlTextBlock(section.body, {
                color: bodyColor,
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.125rem' },
              }),
            })}
          </Stack>
        )}

        {/* MODERN GRID */}
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 3, md: 4 },
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: `repeat(${columns}, minmax(0, 1fr))`,
            },
          }}
        >
          {iconCards.length > 0 ? (
            iconCards.map((card, index) => {
              const baseColor = themeColors[index % themeColors.length];

              return (
                <Box key={`${sectionKey}-icon-card-${index}`}>
                  {renderPositionableElement({
                    section,
                    index: sectionIndex,
                    elementKey: `${sectionKey}-icon-card-${index}`,
                    children: (
                      <Box
                        component={card.link ? 'a' : 'div'}
                        href={card.link || undefined}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          height: '100%',
                          p: { xs: 4, md: 5 },
                          borderRadius: '32px', // Modern large radii
                          backgroundColor: '#FFFFFF',
                          textDecoration: 'none',
                          overflow: 'hidden',
                          cursor: card.link ? 'pointer' : 'default',
                          
                          // Soft borders and tonal backgrounds
                          border: `1px solid ${alpha(baseColor, 0.1)}`,
                          boxShadow: `0 4px 24px ${alpha('#0F172A', 0.03)}`,
                          
                          transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                          
                          // Hover State
                          '&:hover': {
                            transform: card.link ? 'translateY(-8px)' : 'none',
                            backgroundColor: alpha(baseColor, 0.02), // Soft tint on hover
                            borderColor: alpha(baseColor, 0.2),
                            boxShadow: card.link ? `0 20px 40px ${alpha(baseColor, 0.08)}` : undefined,
                            
                            // Animate the arrow
                            '& .card-arrow': {
                              transform: 'translateX(8px)',
                              opacity: 1,
                            },
                            // Scale the icon slightly
                            '& .card-icon': {
                              transform: 'scale(1.1)',
                            }
                          },
                        }}
                      >
                        {/* GIANT WATERMARK NUMBER */}
                        <Typography
                          sx={{
                            position: 'absolute',
                            bottom: -20,
                            right: -10,
                            fontSize: '8rem',
                            fontWeight: 900,
                            color: alpha(baseColor, 0.04),
                            lineHeight: 1,
                            pointerEvents: 'none',
                            userSelect: 'none',
                            zIndex: 0,
                          }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </Typography>

                        <Stack spacing={3} sx={{ position: 'relative', zIndex: 1, height: '100%' }}>
                          
                          {/* SOFT ICON CONTAINER */}
                          <Box
                            className="card-icon"
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: '6px', // Squircle effect
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: baseColor,
                              backgroundColor: alpha(baseColor, 0.1),
                              fontSize: '1.75rem',
                              transition: 'transform 0.4s ease',
                            }}
                          >
                            {card.icon || '•'}
                          </Box>
                          
                          {/* TEXT CONTENT */}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography 
                              component="h3" 
                              sx={{ 
                                color: '#0F172A', 
                                fontWeight: 800, 
                                fontSize: '1.35rem', 
                                mb: 1.5,
                                letterSpacing: '-0.01em'
                              }}
                            >
                              {card.title || 'Card Title'}
                            </Typography>
                            {card.content && (
                              <Typography sx={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.7 }}>
                                {card.content}
                              </Typography>
                            )}
                          </Box>

                          {/* MODERN "LEARN MORE" ARROW (Shows if there's a link) */}
                          {card.link && (
                            <Stack 
                              direction="row" 
                              alignItems="center" 
                              spacing={1} 
                              sx={{ mt: 2, color: baseColor, fontWeight: 700, fontSize: '0.9rem' }}
                            >
                              <span>Learn more</span>
                              <Box
                                className="card-arrow"
                                sx={{
                                  display: 'flex',
                                  transition: 'all 0.3s ease',
                                  opacity: 0.7,
                                }}
                              >
                                {/* Simple SVG Arrow */}
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                  <polyline points="12 5 19 12 12 19"></polyline>
                                </svg>
                              </Box>
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    ),
                  })}
                </Box>
              );
            })
          ) : (
            /* MODERN EMPTY STATE */
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Stack 
                alignItems="center" 
                justifyContent="center" 
                sx={{ 
                  py: 8, 
                  textAlign: 'center', 
                  backgroundColor: '#F8FAFC',
                  border: '2px dashed #CBD5E1', 
                  borderRadius: '32px' 
                }}
              >
                <Typography sx={{ color: '#475569', fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>
                  Ready to add features?
                </Typography>
                <Typography sx={{ color: '#94A3B8', fontSize: '0.95rem' }}>
                  Click in the editor to add beautiful cards to this section.
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default IconGridSection;
